/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

function Country() {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newRecord, setNewRecord] = useState({ name: '', iso_code: '', phone_code: '', currency: '' });
    const [selectedCountry, setSelectedCountry] = useState(null);
    const formRef = useRef(null);

    // Table state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // ─── Filter & Sort ───────────────────────────────────────────────────────────

    const filtered = countries.filter(c =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.iso_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.currency?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = React.useMemo(() => {
        let sortable = [...filtered];
        if (sortConfig.key) {
            sortable.sort((a, b) => {
                let aVal = a[sortConfig.key] ?? '';
                let bVal = b[sortConfig.key] ?? '';
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortable;
    }, [filtered, sortConfig]);

    const totalItems = sorted.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = sorted.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
        return '';
    };

    useEffect(() => { setCurrentPage(1); }, [searchTerm]);

    // ─── API ─────────────────────────────────────────────────────────────────────

    const fetchCountries = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/country');
            setCountries(res.data.data || res.data || []);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to fetch countries' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCountries(); }, []);

    // ─── Handlers ────────────────────────────────────────────────────────────────

    const handleChange = (e) => {
        setNewRecord(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const resetForm = () => {
        setNewRecord({ name: '', iso_code: '', phone_code: '', currency: '' });
        setSelectedCountry(null);
        if (formRef.current) formRef.current.classList.remove('was-validated');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formRef.current.checkValidity()) {
            formRef.current.classList.add('was-validated');
            return;
        }
        try {
            await axiosInstance.post('/api/country', newRecord);
            Swal.fire({ icon: 'success', title: 'Created!', text: 'Country added successfully.', timer: 1500, showConfirmButton: false });
            window.$('#add_country').modal('hide');
            resetForm();
            fetchCountries();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create country' });
        }
    };

    const handleEditClick = (country) => {
        setSelectedCountry(country);
        setNewRecord({ name: country.name || '', iso_code: country.iso_code || '', phone_code: country.phone_code || '', currency: country.currency || '' });
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedCountry) return;
        try {
            await axiosInstance.put(`/api/country/${selectedCountry.id}`, newRecord);
            Swal.fire({ icon: 'success', title: 'Updated!', text: 'Country updated successfully.', timer: 1500, showConfirmButton: false });
            window.$('#edit_country').modal('hide');
            resetForm();
            fetchCountries();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update country' });
        }
    };

    const handleDelete = async (country) => {
        const result = await Swal.fire({
            title: 'Delete Country?',
            text: `Are you sure you want to delete "${country.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete!'
        });
        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/country/${country.id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                fetchCountries();
            } catch {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete country' });
            }
        }
    };

    // ─── Pagination ───────────────────────────────────────────────────────────────

    const Pagination = () => {
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);
        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalItems)} of {totalItems} entries</div>
                <nav>
                    <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(1)}><i className="fa fa-angle-double-left"></i></button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                        </li>
                        {pageNumbers.map(n => (
                            <li key={n} className={`page-item ${n === currentPage ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(n)}>{n}</button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(totalPages)}><i className="fa fa-angle-double-right"></i></button>
                        </li>
                    </ul>
                </nav>
            </div>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Location Management</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <a className="btn btn-primary" href="#" data-bs-toggle="modal" data-bs-target="#add_country" onClick={resetForm}>
                                            <i className="fa fa-plus-circle me-2" aria-hidden="true"></i>Add Country
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="card invoices-tabs-card">
                        <div className="invoices-main-tabs">
                            <div className="row align-items-center">
                                <div className="col-lg-12">
                                    <div className="invoices-tabs">
                                        <ul>
                                            <li><a href="/country" className="active">Country</a></li>
                                            <li><a href="/state">State</a></li>
                                            <li><a href="/city">City</a></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table Controls */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select className="form-select form-select-sm" style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input type="text" className="form-control form-control-sm d-inline-block"
                                                style={{ width: '200px' }} placeholder="Search countries..."
                                                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Country Name{getSortIcon('name')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('iso_code')}>ISO Code{getSortIcon('iso_code')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('phone_code')}>Phone Code{getSortIcon('phone_code')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('currency')}>Currency{getSortIcon('currency')}</th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr><td colSpan="6" className="text-center"><div className="spinner-border" role="status"></div></td></tr>
                                                ) : currentItems.length > 0 ? (
                                                    currentItems.map((country, index) => (
                                                        <tr key={country.id}>
                                                            <td>{startIndex + index + 1}</td>
                                                            <td>{country.name || 'N/A'}</td>
                                                            <td>{country.iso_code || 'N/A'}</td>
                                                            <td>{country.phone_code || 'N/A'}</td>
                                                            <td>{country.currency || 'N/A'}</td>
                                                            <td className="d-flex align-items-center">
                                                                <a className="btn-action-icon bg-success-light me-2" style={{ cursor: 'pointer' }}
                                                                    data-bs-toggle="modal" data-bs-target="#edit_country"
                                                                    onClick={() => handleEditClick(country)} title="Edit">
                                                                    <i className="fa fa-edit"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-danger-light" style={{ cursor: 'pointer' }}
                                                                    onClick={() => handleDelete(country)} title="Delete">
                                                                    <i className="fa fa-trash-alt"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="6" className="text-center">No countries found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                <Footer />
            </div>

            {/* Add Country Modal */}
            <div className="modal custom-modal fade" id="add_country" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0"><i className="fa fa-globe me-2 text-primary"></i>Add Country</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form ref={formRef} onSubmit={handleSubmit} noValidate>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Country Name <span className="text-danger">*</span></label>
                                            <input type="text" name="name" value={newRecord.name} onChange={handleChange}
                                                className="form-control" placeholder="e.g. India" required />
                                            <div className="invalid-feedback">Country name is required.</div>
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6">
                                        <div className="input-block mb-3">
                                            <label>ISO Code</label>
                                            <input type="text" name="iso_code" value={newRecord.iso_code} onChange={handleChange}
                                                className="form-control" placeholder="e.g. IN" maxLength={3} />
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Phone Code</label>
                                            <input type="text" name="phone_code" value={newRecord.phone_code} onChange={handleChange}
                                                className="form-control" placeholder="e.g. +91" />
                                        </div>
                                    </div>
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Currency</label>
                                            <input type="text" name="currency" value={newRecord.currency} onChange={handleChange}
                                                className="form-control" placeholder="e.g. INR" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">Cancel</button>
                                <button type="submit" className="btn btn-primary paid-continue-btn">Add Country</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Edit Country Modal */}
            <div className="modal custom-modal fade" id="edit_country" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0"><i className="fa fa-edit me-2 text-primary"></i>Edit Country</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>Country Name <span className="text-danger">*</span></label>
                                        <input type="text" name="name" value={newRecord.name} onChange={handleChange}
                                            className="form-control" placeholder="e.g. India" />
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6">
                                    <div className="input-block mb-3">
                                        <label>ISO Code</label>
                                        <input type="text" name="iso_code" value={newRecord.iso_code} onChange={handleChange}
                                            className="form-control" placeholder="e.g. IN" maxLength={3} />
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6">
                                    <div className="input-block mb-3">
                                        <label>Phone Code</label>
                                        <input type="text" name="phone_code" value={newRecord.phone_code} onChange={handleChange}
                                            className="form-control" placeholder="e.g. +91" />
                                    </div>
                                </div>
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>Currency</label>
                                        <input type="text" name="currency" value={newRecord.currency} onChange={handleChange}
                                            className="form-control" placeholder="e.g. INR" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">Cancel</button>
                            <button type="button" onClick={handleUpdate} className="btn btn-primary paid-continue-btn">Update Country</button>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
}

export default Country;